'use client';

import type { IconButtonProps, SpanProps } from '@chakra-ui/react';
import { ClientOnly, IconButton, Skeleton, Span } from '@chakra-ui/react';
import { ThemeProvider, useTheme } from 'next-themes';
import type { ThemeProviderProps } from 'next-themes';
import * as React from 'react';
import { LuMoon, LuSun } from 'react-icons/lu';

// Custom icon: circle split half light, half dark for system/auto mode
const LuHalfCircle = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3v18" />
    <path d="M12 3a9 9 0 0 1 0 18" fill="currentColor" />
  </svg>
);

export type ColorModeProviderProps = ThemeProviderProps;

/**
 * @useFor wrapping the app root to enable light/dark/system theme
 *   switching. Mount once near `<App>`; consumer code uses
 *   `useColorMode` / `useColorModeValue` / `<ColorModeButton>`.
 *
 *   Examples in-tree: every app root (SMS, AIM, ONE, Beacon).
 *
 *   For app-level theming beyond light/dark (brand palettes, custom
 *   color tokens) configure the Chakra theme directly — this
 *   provider only handles the light/dark/system axis.
 */
export function ColorModeProvider(props: ColorModeProviderProps) {
  return (
    <ThemeProvider attribute="class" disableTransitionOnChange {...props} />
  );
}

export type ColorMode = 'light' | 'dark';
export type ThemeSetting = 'light' | 'dark' | 'system';

export interface UseColorModeReturn {
  /** The resolved color mode (always 'light' or 'dark') */
  colorMode: ColorMode;
  /** The user's theme preference ('light', 'dark', or 'system') */
  themeSetting: ThemeSetting;
  /** Set the color mode/theme setting */
  setColorMode: (colorMode: ThemeSetting) => void;
  /** Cycle through: light -> dark -> system -> light */
  toggleColorMode: () => void;
}

/**
 * @useFor reading and toggling the current theme inside a component.
 *   Returns `{ colorMode, themeSetting, setColorMode, toggleColorMode }`
 *   where `colorMode` is the resolved value (`'light'` | `'dark'`)
 *   and `themeSetting` is the user's preference (which may be
 *   `'system'`).
 *
 *   For color-mode-conditional values inside JSX prefer
 *   `useColorModeValue(light, dark)` — clearer than branching on
 *   `colorMode` and easier to grep for.
 */
export function useColorMode(): UseColorModeReturn {
  const { resolvedTheme, theme, setTheme } = useTheme();

  // Cycle through: light -> dark -> system -> light
  const toggleColorMode = () => {
    const nextTheme =
      theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
    setTheme(nextTheme);
  };

  return {
    colorMode: resolvedTheme as ColorMode,
    themeSetting: (theme as ThemeSetting) || 'system',
    setColorMode: setTheme,
    toggleColorMode,
  };
}

export function useColorModeValue<T>(light: T, dark: T) {
  const { colorMode } = useColorMode();
  return colorMode === 'dark' ? dark : light;
}

export function ColorModeIcon() {
  const { themeSetting } = useColorMode();
  if (themeSetting === 'system') return <LuHalfCircle />;
  return themeSetting === 'dark' ? <LuMoon /> : <LuSun />;
}

type ColorModeButtonProps = Omit<IconButtonProps, 'aria-label'>;

/**
 * @useFor a single-tap theme toggle in app chrome — cycles
 *   light → dark → system → light. Renders the icon for the
 *   current setting with an aria-label describing what tapping
 *   will do next.
 *
 *   Examples in-tree: app sidebar footer, user-menu dropdown.
 *
 *   For a multi-option theme picker (explicit Light / Dark / System
 *   choices visible at once) use `<ToggleGroup>` against the
 *   `useColorMode` setter instead — this button is the compact
 *   "one click to rotate" pattern.
 */
export const ColorModeButton = React.forwardRef<
  HTMLButtonElement,
  ColorModeButtonProps
>(function ColorModeButton(props, ref) {
  const { toggleColorMode, themeSetting } = useColorMode();
  const labels: Record<ThemeSetting, string> = {
    light: 'Light mode (click for dark)',
    dark: 'Dark mode (click for system)',
    system: 'System mode (click for light)',
  };
  return (
    <ClientOnly fallback={<Skeleton boxSize="10" borderRadius="8px" />}>
      <IconButton
        onClick={toggleColorMode}
        aria-label={labels[themeSetting]}
        title={labels[themeSetting]}
        size="md"
        ref={ref}
        bg="#E5E5EA"
        color="#1C1C1E"
        borderRadius="8px"
        _hover={{ bg: '#D1D1D6' }}
        {...props}
      >
        <ColorModeIcon />
      </IconButton>
    </ClientOnly>
  );
});

export const LightMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function LightMode(props, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme light"
        colorPalette="gray"
        colorScheme="light"
        ref={ref}
        {...props}
      />
    );
  },
);

export const DarkMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function DarkMode(props, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme dark"
        colorPalette="gray"
        colorScheme="dark"
        ref={ref}
        {...props}
      />
    );
  },
);
